import axios from "axios";
import { getFreshIdToken } from "../auth/getFreshIdToken";
import { readPersistedWorkspace } from "../utils/workspacePersistence";

/**
 * Exchange an OAuth authorization code for tokens ON THE SERVER.
 *
 * Google Business Profile, YouTube and Facebook/Instagram used to do this in the
 * browser with the client secret bundled into the app (REACT_APP_*_CLIENT_SECRET),
 * which made the secrets public. social-management's POST /{customer_id}/oauth/token
 * holds the secrets and returns the provider's own token response, so callers read
 * the same fields they always did (access_token, refresh_token, id_token, …).
 *
 * The Brand in the path is the one the owner is working in (they started the
 * connect from it); callers that let the owner pick a Brand afterwards still do.
 */
export type OAuthProvider = "google" | "youtube" | "meta" | "instagram";

const SOCIAL_API_BASE = process.env.REACT_APP_SOCIAL_API_URL || "";

export async function exchangeOAuthCode<T = Record<string, any>>(
  provider: OAuthProvider,
  code: string,
  redirectUri: string
): Promise<T> {
  if (!SOCIAL_API_BASE) throw new Error("REACT_APP_SOCIAL_API_URL is not set.");
  const customerId = readPersistedWorkspace().id;
  if (!customerId) throw new Error("Choose a business first, then connect again.");
  const idToken = await getFreshIdToken();
  if (!idToken) throw new Error("Please sign in again, then connect.");
  try {
    const { data } = await axios.post<T>(
      `${SOCIAL_API_BASE.replace(/\/$/, "")}/${encodeURIComponent(customerId)}/oauth/token`,
      { provider, code, redirect_uri: redirectUri },
      { headers: { Authorization: `Bearer ${idToken}`, "Content-Type": "application/json" } }
    );
    return data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const msg = (error.response?.data as { error?: string } | undefined)?.error;
      if (msg) throw new Error(msg);
    }
    throw error;
  }
}
